import { Injectable } from '@angular/core';
import { MarineInsuranceModel, MarineInsuranceSearchShipmentModel } from '@data-layer/models/marine-insurance';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { MarineInsuranceFacade } from './marine-insurance.facade';
import { catchError, switchMap } from 'rxjs/operators';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { enumEntrySummaryState, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { enumIsfState } from '@data-layer/enums/isf';
import { EntrySummaryStatusHelper } from '@helpers/entry-summary-status.helper';
import { IsfHelper } from '@helpers/isf.helper';
import { enumMarineInsuranceStatusType } from '@data-layer/enums/marine-insurance/enumInsuranceStatusType.enum';
import { enumLclInsuranceBlockReason } from '@data-layer/enums/marine-insurance';
import { enumLclTrackingStates } from '@enum/enumLclTrackingStates';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { DateTimezoneService } from '@services/date-timezone.service';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { AbstractControl, ValidatorFn } from '@angular/forms';

/** Minimal shape the marine-insurance shield needs from a shipment. */
export interface IInsuranceIndicatorShipment {
  exportDate?: string;
}

export interface IInsuranceIndicatorEntrySummary {
  insuranceStatus?: enumMarineInsuranceStatusType;
  state?: enumEntrySummaryState;
  transportationMode?: enumTransportationMode;
  shipments?: IInsuranceIndicatorShipment[];
}

export interface IInsuranceIndicatorIsf {
  insuranceStatus?: enumMarineInsuranceStatusType;
  state?: enumIsfState;
  shipment?: IInsuranceIndicatorShipment;
}

export interface IInsuranceIndicatorLclVoyage {
  route?: { arrivalPort?: { country?: { code?: string } } };
  actualDepartureTime?: string;
  estimatedDepartureTime?: string;
}

export interface IInsuranceIndicatorLclShipment {
  insuranceStatus?: enumMarineInsuranceStatusType;
  trackingState?: enumLclTrackingStates;
  voyage?: IInsuranceIndicatorLclVoyage;
}

@Injectable({ providedIn: 'root' })
export class MarineInsuranceAvailabilityService {
  constructor(
    private marineInsuranceFacade: MarineInsuranceFacade,
    private dateTimezoneService: DateTimezoneService,
    private companiesService: CompaniesService,
  ) {}

  public isShowCreateForIsf(isfReferenceNumber: string): Observable<{ insurance: MarineInsuranceModel; isAvailable: boolean }> {
    return this.marineInsuranceFacade.getShipments({ isfReferenceNumber }).pipe(
      switchMap((insurance: MarineInsuranceModel) => {
        if (!insurance?.id) {
          throw new Error('No insurance added');
        }
        if (insurance?.certificate?.issueDate) {
          return of({ insurance, isAvailable: !insurance?.id });
        }
        if (this.validateDates(enumTransportationMode.OCEAN, insurance?.shippingDate)) {
          throwError({ insurance: null, isAvailable: false });
        } else {
          if (!insurance?.certificate?.issueDate) {
            return EMPTY;
          }
        }
        return of({ insurance, isAvailable: !insurance?.id });
      }),
      catchError(() => throwError({ insurance: null, isAvailable: false })),
    );
  }

  public isShowCreateForEntrySummary(es: EntrySummaryModel): Observable<{ insurance: MarineInsuranceModel; isAvailable: boolean }> {
    // TODO remove it later
    if (es?.shipments?.length > 1 || [enumTransportationMode.RAIL, enumTransportationMode.TRUCK].includes(es?.transportationMode)) {
      return EMPTY;
    }

    return this.marineInsuranceFacade.getShipments({ entrySummaryReferenceNumber: es.referenceNumber }).pipe(
      switchMap((insurance: MarineInsuranceModel) => {
        if (insurance?.certificate?.issueDate) {
          return of({ insurance, isAvailable: !insurance?.id });
        }

        if (!!es?.shipments?.[0]?.exportDate) {
          if (this.validateDates(es.transportationMode, es.shipments?.[0]?.exportDate)) {
            return of({ insurance, isAvailable: !insurance?.id });
          } else {
            if (!insurance?.certificate?.issueDate) {
              return EMPTY;
            }
          }
        }

        if (!insurance?.id) {
          throw new Error('No insurance added');
        }

        return of({ insurance, isAvailable: !insurance?.id });
      }),
      catchError(() => throwError({ insurance: null, isAvailable: false })),
    );
  }

  public isShowCreateForShipment(data: {
    freightShipment?: LCLShipmentModel;
    freightQuote?: QuoteModel;
  }): Observable<{ insurance: MarineInsuranceModel; isAvailable: boolean }> {
    const objToSearch: MarineInsuranceSearchShipmentModel = {};
    if (!!data.freightShipment?.referenceNumber) {
      objToSearch['freightShipmentReferenceNumber'] = data.freightShipment.referenceNumber;
    }
    if (!!data.freightQuote?.referenceNumber) {
      objToSearch['freightQuoteReferenceNumber'] = data.freightQuote.referenceNumber;
    }

    return this.marineInsuranceFacade.getShipments(objToSearch).pipe(
      switchMap((insurance: MarineInsuranceModel) => {
        if (insurance?.certificate?.issueDate) {
          return of({ insurance, isAvailable: !insurance?.id });
        }

        if (
          (!!data.freightShipment &&
            !this.validateDates(
              enumTransportationMode.OCEAN,
              data.freightShipment?.voyage?.actualDepartureTime || data.freightShipment?.voyage?.estimatedDepartureTime,
            )) ||
          (!!data.freightQuote &&
            !this.validateDates(
              enumTransportationMode.OCEAN,
              data.freightQuote?.voyage?.actualDepartureTime || data.freightQuote?.voyage?.estimatedDepartureTime,
            ))
        ) {
          return EMPTY;
        }

        if (!insurance?.id) {
          throw new Error('No insurance added');
        }

        return of({ insurance, isAvailable: !insurance?.id });
      }),
      catchError(() => throwError({ insurance: null, isAvailable: false })),
    );
  }

  /**
   * Whether the marine-insurance shield should be shown for an entry summary.
   * Rail/Truck entries never show it, even if insured before the broker changed the type (#13412).
   * Otherwise an already-insured entry always keeps the indicator so its certificate stays downloadable,
   * no matter how long ago the shipment departed. A draft is never offered insurance (#13417).
   */
  public isShowIndicatorForEntrySummary(es: IInsuranceIndicatorEntrySummary): boolean {
    const transportationMode: enumTransportationMode = es?.transportationMode;

    if (transportationMode === enumTransportationMode.RAIL || transportationMode === enumTransportationMode.TRUCK) {
      return false;
    }

    if (es?.insuranceStatus === enumMarineInsuranceStatusType.INSURED) {
      return true;
    }

    if (!EntrySummaryStatusHelper.isSent(es?.state)) {
      return false;
    }

    const shipments: IInsuranceIndicatorShipment[] = es?.shipments;

    return (
      shipments?.length === 1 && // marine insurance is not offered for multi-shipment entries
      Boolean(this.validateDates(transportationMode, shipments[0]?.exportDate))
    );
  }

  /**
   * ISF counterpart of {@link isShowIndicatorForEntrySummary}; an ISF is always a single ocean shipment.
   * A draft is never offered insurance (#13417).
   */
  public isShowIndicatorForIsf(isf: IInsuranceIndicatorIsf): boolean {
    if (isf?.insuranceStatus === enumMarineInsuranceStatusType.INSURED) {
      return true;
    }

    if (!IsfHelper.isSent(isf?.state)) {
      return false;
    }

    const exportDate: string = isf?.shipment?.exportDate;
    const hasExportDate: boolean = Boolean(exportDate);

    // no export date yet — the purchase window cannot be ruled out, so keep offering it
    return !hasExportDate || this.validateDates(enumTransportationMode.OCEAN, exportDate);
  }

  /**
   * Shield visible for an LCL shipment: already insured, or a US-bound voyage
   * (marine insurance is only sold for shipments arriving in the US).
   */
  public isShowIndicatorForLclShipment(shipment: IInsuranceIndicatorLclShipment): boolean {
    if (shipment?.insuranceStatus === enumMarineInsuranceStatusType.INSURED) {
      return true;
    }

    return shipment?.voyage?.route?.arrivalPort?.country?.code === USA_COUNTRY_CODE;
  }

  /**
   * Why marine insurance can no longer be bought for an LCL shipment, or `null` while it still can.
   * Tracking state is checked first so the more specific reason wins over the generic date window.
   */
  public getLclShipmentInsuranceBlockReason(shipment: IInsuranceIndicatorLclShipment): enumLclInsuranceBlockReason | null {
    const trackingState: enumLclTrackingStates = shipment?.trackingState;

    if (trackingState === enumLclTrackingStates.AT_DESTINATION_WAREHOUSE || trackingState === enumLclTrackingStates.DELIVERED) {
      return enumLclInsuranceBlockReason.TRACKING_STATE;
    }

    const voyage: IInsuranceIndicatorLclVoyage = shipment?.voyage;
    const departureDate: string = voyage?.actualDepartureTime ?? voyage?.estimatedDepartureTime;

    if (!this.validateDates(enumTransportationMode.OCEAN, departureDate)) {
      return enumLclInsuranceBlockReason.SAILING_WINDOW;
    }

    return null;
  }

  public validateDates(transportationMode: enumTransportationMode, exportDate: string): boolean {
    let result: boolean = false;
    let minVal: number = 0;
    const maxVal: number = 28;

    if (!exportDate || !transportationMode) {
      return;
    }

    const dayDiff: number = this.dateTimezoneService.daysBetween(
      this.dateTimezoneService.startOfTheDay(new Date().toString()),
      this.dateTimezoneService.startOfTheDay(exportDate),
    );

    if (transportationMode === enumTransportationMode.AIR) {
      result = dayDiff >= minVal && dayDiff <= maxVal;
    }

    if (transportationMode === enumTransportationMode.OCEAN) {
      minVal = -21;

      result = dayDiff >= minVal && dayDiff <= maxVal;
    }

    return result;
  }

  public isfInsuranceDateValidator(errorField: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const value: string = control.value;

      if (!control) {
        return null;
      }

      const isInvalid: boolean = !value || !this.validateDates(enumTransportationMode.OCEAN, value);

      if (isInvalid) {
        return { [errorField]: true };
      }
    };
  }
}
